import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { PIPELINE_STAGE_LABELS, PIPELINE_STAGE_COLORS, type PipelineStage, serviceTypeLabel } from "@/types/quotes";
import type { QuoteRow } from "@/hooks/useQuotes";
import type { QuoteSortConfig } from "@/hooks/useFilteredQuotes";
import { formatRelativeTimeCompact } from "@/lib/format-relative-time";

const STATUS_STYLES: Record<string, { className: string; label: string }> = {
  draft: { className: "bg-muted text-muted-foreground", label: "Draft" },
  sent: { className: "bg-primary/10 text-primary border-primary/30", label: "Sent" },
  accepted: { className: "bg-success/20 text-foreground", label: "Accepted" },
  declined: { className: "bg-destructive/10 text-destructive", label: "Declined" },
};

interface QuotesListViewProps {
  quotes: QuoteRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  sort: QuoteSortConfig;
  onPageChange: (page: number) => void;
  onSortChange: (sort: QuoteSortConfig) => void;
  onViewQuote: (id: string) => void;
  onContactClick: (contactId: string) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  isLoading?: boolean;
}

// Columns config: sortable = true means column header is clickable for sorting
const COLUMNS: { key: string; label: string; className?: string; sortable?: boolean }[] = [
  { key: "quote_number", label: "Quote #", sortable: true },
  { key: "client_name", label: "Client", sortable: true },
  { key: "pipeline_stage", label: "Stage", sortable: true },
  { key: "event_date", label: "Event Date", sortable: true },
  { key: "service_type", label: "Service", sortable: true },
  { key: "guest_count", label: "Guests", sortable: true },
  { key: "total", label: "Total", className: "text-right", sortable: true },
  { key: "amount_paid", label: "Paid", className: "text-right", sortable: true },
  { key: "status", label: "Status", sortable: true },
  { key: "pipeline_stage_changed_at", label: "Stage Changed", sortable: true },
  { key: "last_contacted", label: "Last Contacted", sortable: false },
];

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}


export function QuotesListView({
  quotes, totalCount, page, pageSize, sort, onPageChange, onSortChange,
  onViewQuote, onContactClick, selectedIds, onSelectionChange, isLoading,
}: QuotesListViewProps) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalCount);

  const allSelected = quotes.length > 0 && quotes.every((q) => selectedIds.has(q.id));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(quotes.map((q) => q.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const handleSort = (column: string) => {
    if (sort.column === column) {
      onSortChange({ column, ascending: !sort.ascending });
    } else {
      onSortChange({ column, ascending: true });
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sort.column !== column) return null;
    return sort.ascending
      ? <ArrowUp className="h-3 w-3 inline ml-1" />
      : <ArrowDown className="h-3 w-3 inline ml-1" />;
  };

  return (
    <div className="space-y-3">
      <Card>
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                </TableHead>
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    className={`${col.sortable !== false ? "cursor-pointer select-none hover:text-foreground transition-colors" : ""} ${col.className || ""}`}
                    onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}
                    {col.sortable !== false && <SortIcon column={col.key} />}
                  </TableHead>
                ))}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12 text-muted-foreground">No quotes match your filters.</TableCell>
                </TableRow>
              ) : (
                quotes.map((q) => {
                  const s = STATUS_STYLES[q.status] ?? STATUS_STYLES.draft;
                  const stage = q.pipeline_stage as PipelineStage;
                  return (
                    <TableRow
                      key={q.id}
                      className="cursor-pointer h-10"
                      onClick={() => onViewQuote(q.id)}
                    >
                      <TableCell className="py-1.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedIds.has(q.id)} onCheckedChange={() => toggleOne(q.id)} />
                      </TableCell>
                      <TableCell className="font-mono text-xs py-1.5">
                        <div className="flex items-center gap-1.5">
                          {q.quote_number}
                          {q.square_source && (
                            <span title="Square" className="inline-flex items-center justify-center w-4 h-4 rounded bg-primary/10 text-primary text-[9px] font-bold shrink-0">S</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div>
                          {q.contact_id ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); onContactClick(q.contact_id!); }}
                              className="font-medium text-sm hover:text-primary hover:underline transition-colors"
                            >
                              {q.client_name}
                            </button>
                          ) : (
                            <span className="font-medium text-sm">{q.client_name}</span>
                          )}
                        </div>
                        {q.event_type?.name && (
                          <div className="text-[11px] text-muted-foreground">{q.event_type.name}</div>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Badge className={`${PIPELINE_STAGE_COLORS[stage] || "bg-muted text-muted-foreground"} text-[10px] px-1.5 py-0`}>
                          {PIPELINE_STAGE_LABELS[stage] || stage}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs py-1.5">{formatDate(q.event_date)}</TableCell>
                      <TableCell className="text-xs py-1.5">
                        {serviceTypeLabel(q.service_type, q.fulfillment_type).split(/[\s(]/)[0]}
                      </TableCell>
                      <TableCell className="text-xs py-1.5">{q.guest_count}</TableCell>
                      <TableCell className="text-right font-medium text-sm py-1.5">${Number(q.total).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-xs py-1.5">
                        {Number(q.amount_paid ?? 0) > 0 ? (
                          <span className={Number(q.amount_paid) >= Number(q.total) ? "text-success-foreground font-medium" : "text-warning"}>
                            ${Number(q.amount_paid).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Badge className={`${s.className} text-[10px]`}>{s.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs py-1.5 text-muted-foreground" title={q.pipeline_stage_changed_at ? new Date(q.pipeline_stage_changed_at).toLocaleString() : undefined}>
                        {formatRelativeTimeCompact(q.pipeline_stage_changed_at)}
                      </TableCell>
                      <TableCell className="text-xs py-1.5 text-muted-foreground" title={q.contact?.last_message_at ? new Date(q.contact.last_message_at).toLocaleString() : undefined}>
                        {formatRelativeTimeCompact(q.contact?.last_message_at)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onViewQuote(q.id); }}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground text-xs">
            Showing {from}–{to} of {totalCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {/* Show up to 5 page buttons around current */}
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (page <= 2) {
                pageNum = i;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8 text-xs"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum + 1}
                </Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
